import isMobile from 'is-mobile';
import { useTranslation } from 'react-i18next';

import { Box, Button, forwardRef, IconButton, StyleProps, ThumbUpOutline, usePopoverContext } from '@bonusly/design-system';

import { useTrackReaction } from '../../components/reactions/hooks/use-track-reaction';
import { useReactionContext } from '../../components/reactions/reaction-select-context';
import { useLongPress } from '../../hooks/use-long-press';
import { buttonStyle, compactButtonStyle } from './styles';
import { ReactionType } from './types';

export const ReactionButtonEmpty = forwardRef<StyleProps, 'button'>((props, ref) => {
	const { t } = useTranslation('recognition');
	const { createReaction, isLoading, onToggle, variant } = useReactionContext();
	const { onClose } = usePopoverContext();
	const track = useTrackReaction();

	const handleClickAdd = () => {
		track.quick();
		createReaction(ReactionType.Like);
		onClose();
	};

	// handles hover desktop and longpress on mobile
	const onLongPress = () => {
		if (isMobile()) onToggle();
	};

	const eventHandlers = useLongPress({
		onLongPress,
		onClick: handleClickAdd,
	});

	if (variant === 'compact') {
		return (
			<IconButton
				aria-label={t('reactions.actions.add') ?? ''}
				data-testid="enhanced-reactions"
				ref={ref}
				isDisabled={isLoading}
				icon={<ThumbUpOutline />}
				{...eventHandlers}
				{...compactButtonStyle}
				{...props}
			/>
		);
	}

	return (
		<Button
			aria-label={t('reactions.actions.add') ?? ''}
			data-testid="enhanced-reactions"
			ref={ref}
			isDisabled={isLoading}
			{...eventHandlers}
			{...buttonStyle}
			{...props}
		>
			<ThumbUpOutline size="md" />
			<Box as="span" display={{ base: 'none', xs: 'inline-block' }} ml={2}>
				{t('reactions.name.like')}
			</Box>
		</Button>
	);
});
